<template>
  <PageHeader
    breadcrumb
    :items="[{ label: $t('ui.entities.campaign', 2), to: '/customers/campaigns' }, { label: campaignData?.name }]"
  >
    <template #right>
      <div v-if="campaignData">
        <Button
          v-if="$can('manage_campaigns')"
          label="Delete"
          outlined
          icon="trash"
          severity="danger"
          class="mr-3"
          @click="deleteCampaign"
        />
        <!-- <Button label="Edit" icon="plus" class="mr-3" /> -->
      </div>
    </template>
  </PageHeader>

  <div
    v-if="campaignData?.prospects?.length"
    class="flex gap-0 flex-col md:flex-row md:gap-4"
  >
    <Metric
      :model-value="metrics.totalCustomers"
      compact
      icon="users"
      :has-graph="false"
      title="Total Customers"
    />
    <Metric
      :model-value="metrics.contacted"
      compact
      icon="phone"
      :has-graph="false"
      title="Contacted"
    />
    <Metric
      :model-value="metrics.appointed"
      compact
      icon="calendar"
      :has-graph="false"
      title="Appointed"
    />
    <Metric
      :model-value="metrics.dealt"
      compact
      icon="check"
      :has-graph="false"
      title="Dealt"
    />
    <Metric
      :model-value="metrics.archived"
      compact
      icon="archive"
      :has-graph="false"
      title="Archived"
    />
  </div>

  <div
    v-else
    class="flex gap-0 flex-col md:flex-row md:gap-4 mb-4"
  >
    <Skeleton
      width="100%"
      height="82px"
    />
    <Skeleton
      width="100%"
      height="82px"
    />
    <Skeleton
      width="100%"
      height="82px"
    />
    <Skeleton
      width="100%"
      height="82px"
    />
  </div>

  <Card>
    <DataTable
      v-if="campaignIsLoading || campaignData"
      ref="dt"
      v-model:filters="filters"
      :value="prospectsComputed"
      removable-sort
      sort-mode="multiple"
      :sort-order="1"
      :rows="20"
      data-key="id"
      :paginator="true"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-campaign-prospects-list"
      state-storage="session"
      :loading="campaignIsLoading"
      :global-filter-fields="['id', 'name']"
    >
      <template #empty>No records found</template>
      <template #header>
        <div class="flex justify-between items-center gap-4">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <div class="toggle-container">
              <ToggleSwitch
                v-model="showArchivedCustomers"
                size="sm"
              />
              <div class="font-medium">Show Archived Customers</div>
            </div>
            <div class="toggle-container">
              <ToggleSwitch
                v-model="includeOffers"
                size="sm"
              />
              <div class="font-medium">Include Offers</div>
            </div>
            <Button
              :label="$t('ui.actions.reset', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
          <Select
            v-model="columnsSelected"
            :options="columns"
            label-key="header"
            value-key="field"
            :chips="false"
            mode="multiple"
            on-select-return-as-object
            class="max-w-[220px]"
            placeholder="Select Columns"
            @change="onToggle"
          />
        </div>
      </template>

      <!-- <Column selection-mode="multiple" header-style="width: 3em"></Column> -->
      <!-- <Column field="id" header="#" :sortable="true">
        <template #body="{ data }">
          <a class="link" :href="'/agreement/' + data.agreementID">{{ data.agreementID }}</a>
        </template>
      </Column> -->
      <Column
        v-if="isColumnVisible('name')"
        field="name"
        header="Customer"
        :sortable="true"
      >
        <template #body="{ data }">
          <div class="flex">
            <router-link
              :to="'/agreement/' + data.agreementID"
              class="font-medium link"
            >
              {{ data.name }}
            </router-link>
            <Icon
              v-if="data.isArchived"
              type="box-archive"
              class="ml-2"
            />
          </div>
        </template>
      </Column>
      <Column
        v-if="1 == 0"
        field="model"
        header="Vehicle"
        :sortable="true"
      >
        <template #body="{ data }">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <img
                v-tippy="{ content: data.make }"
                :src="data.manufacturerLogo"
                class="max-h-4 w-4 mr-2"
              />
              {{ data.model }}
            </div>

            <CopyText>
              <LicensePlate
                :plate="data.vrm"
                country="uk"
                :design="data.fuelType == 'electric' ? 'ev' : ''"
                greyscale
                size="xs"
                class="ml-2"
              />
            </CopyText>
          </div>
        </template>
      </Column>

      <Column
        v-if="isColumnVisible('monthsRemaining')"
        field="monthsRemaining"
        header="Months Remaining"
        :sortable="true"
        class="!py-0"
      >
        <template #body="{ data }">
          <div class="flex items-center">
            <ProgressCircle
              v-model="data.termCompletion"
              v-model:displayed-value="data.monthsRemaining"
              size="sm"
            />
          </div>
        </template>
      </Column>

      <Column
        v-if="isColumnVisible('contractType')"
        field="contractType"
        header="Product"
        :sortable="false"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
        :filter-menu-style="{ 'max-width': '24rem' }"
      >
        <template #body="{ data }">
          {{ data.contractType }}
        </template>
        <template #filter="{ filterModel }">
          <div class="mb-3 font-bold">Contract Type</div>
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="contractTypesList"
            value-key="value"
            label-key="label"
            mode="multiple"
            placeholder="Any"
            class="p-column-filter"
          >
            <template #option="slotProps">
              {{ slotProps.option.value }}
            </template>
          </Select>
        </template>
      </Column>

      <Column
        v-if="isColumnVisible('equity')"
        field="displayEquity"
        header="Equity"
        :sortable="true"
        class="whitespace-nowrap"
      >
        <template #body="{ data }">
          {{ data.displayEquity !== null ? formatNumber(data.displayEquity, 'currency', 0, 'GBP') : '-' }}
        </template>
      </Column>

      <Column
        v-if="isColumnVisible('opportunity')"
        field="displayOpportunityLabel"
        sort-field="opportunityWeight"
        header="Opportunity"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
        :filter-menu-style="{ 'max-width': '24rem' }"
      >
        <template #body="{ data }">
          <div class="flex items-center gap-2">
            <Icon
              v-if="data.displayOpportunityLabel"
              v-tippy="getDictionaryItem('agreementOpportunity', data.displayOpportunityLabel)?.label"
              :type="getDictionaryItem('agreementOpportunity', data.displayOpportunityLabel)?.icon"
              class="text-xl"
              :class="'text-' + getDictionaryItem('agreementOpportunity', data.displayOpportunityLabel)?.color"
            />
            <Chip
              v-if="includeOffers && data.vehicleOfferEquity !== null"
              v-tippy="'Equity + Offer DC'"
              size="md"
              :rounded="false"
              outlined
              icon="badge-percent"
              severity="success"
            >
              {{ formatNumber(data.vehicleOfferEquity, 'currency', 0, 'GBP') }}
            </Chip>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <div class="mb-3 font-bold">Opportunity</div>
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="opportunityOptions"
            value-key="key"
            label-key="label"
            mode="multiple"
            placeholder="Any"
            class="p-column-filter"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>

      <Column
        v-if="isColumnVisible('status')"
        field="status"
        header="Status"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
        :filter-menu-style="{ 'max-width': '24rem' }"
      >
        <template #body="{ data }">
          <Chip
            :severity="getDictionaryItem('agreementStatus', data.status)?.color"
            label
            :icon="getDictionaryItem('agreementStatus', data.status)?.icon"
          >
            {{ data.status }}
          </Chip>
        </template>
        <template #filter="{ filterModel }">
          <div class="mb-3 font-bold">Status</div>
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="statusesList"
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Any"
            class="p-column-filter"
          >
            <template #option="slotProps">
              {{ slotProps.option.value }}
            </template>
          </Select>
        </template>
      </Column>

      <Column
        v-if="isColumnVisible('tags')"
        field="tags"
        header="Tags"
        :sortable="false"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
        :filter-menu-style="{ 'max-width': '24rem' }"
      >
        <template #body="{ data }">
          <Chip
            v-for="tag in data.tags"
            :key="tag"
            class="mr-1"
            size="sm"
            :severity="tag.displayStyle"
          >
            {{ tag.title }}
          </Chip>
        </template>
        <template #filter="{ filterModel }">
          <div class="mb-3 font-bold">Tags</div>
          <Select
            :teleport="false"
            v-model="filterModel.value"
            label-key="title"
            value-key="id"
            searchable
            mode="multiple"
            :options="tagsList"
            :chips="false"
            placeholder="Any"
            class="min-w-64"
          >
            <template #option="slotProps">
              {{ slotProps.option.title }}
            </template>
          </Select>
        </template>
      </Column>

      <Column
        v-if="campaignData?.type == 'standard' && isColumnVisible('lastCalled')"
        field="lastCalled"
        header="Last Called"
        :sortable="true"
        class="whitespace-nowrap"
      >
        <template #body="{ data }">
          <Icon
            v-if="data.lastCalled"
            type="phone"
            class="mr-2 text-quaternary"
          />
          <span
            v-tippy="{ content: data.lastCallData.tippy }"
            class="whitespace-nowrap"
            :class="'text-' + data.lastCallData.color"
          >
            {{ data.lastCallData.text }}
          </span>
        </template>
      </Column>

      <Column
        v-if="campaignData?.type == 'standard' && isColumnVisible('latestAppointment')"
        field="latestAppointment"
        header="Appointment"
        :sortable="true"
        class="whitespace-nowrap"
      >
        <template #body="{ data }">
          <Icon
            v-if="data.latestAppointment"
            type="calendar"
            class="mr-2 text-quaternary"
          />
          <span
            v-tippy="{ content: dayjs(data.latestAppointment).format('lll') }"
            class="whitespace-nowrap"
          >
            {{ data.latestAppointment ? dayjs(data.latestAppointment).format('ll') : '' }}
          </span>
        </template>
      </Column>

      <Column
        v-if="campaignData?.type == 'marketing' && isColumnVisible('inviteStatus')"
        field="inviteStatus"
        header="Invite Status"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
        :filter-menu-style="{ 'max-width': '24rem' }"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.inviteStatus"
            v-tippy="{ content: data.latestAppointment }"
            :severity="data.inviteStatusColor"
            :icon="data.inviteStatusIcon"
            class="whitespace-nowrap"
          >
            {{ capitalize(data.inviteStatus) }}
          </Chip>
        </template>
        <template #filter="{ filterModel }">
          <div class="mb-3 font-bold">Invite Status</div>
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="inviteStatusOptions"
            searchable
            mode="multiple"
            value-key="value"
            label-key="label"
            placeholder="Any"
            class="p-column-filter"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>

      <Column v-if="$can('manage_campaigns')">
        <template #body="{ data }">
          <Dropdown>
            <template #triggerContent>
              <Button
                icon="ellipsis-vertical"
                plain
                severity="secondary"
              ></Button>
            </template>
            <DropdownItem @item-clicked="removeProspect(data.id)">
              <Icon
                type="trash"
                class="text-error"
              />
              <span class="w-full">Remove</span>
            </DropdownItem>
          </Dropdown>
        </template>
      </Column>
    </DataTable>
    <CardBody v-else>Campaign Not Found</CardBody>
  </Card>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue'
import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import Button from '@/components/button/Button.vue'
import Chip from '@/components/chip/Chip.vue'
import Icon from '@/components/icon/Icon.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import DropdownItem from '@/components/dropdown/DropdownItem.vue'
import ProgressCircle from '@/components/progress/ProgressCircle.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'
import Select from '@/components/forms/Select.vue'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode, FilterService } from '@primevue/core/api'

import CopyText from '@/components/CopyText.vue'
import LicensePlate from '@/components/license-plate/LicensePlate.vue'
import Input from '@/components/forms/Input.vue'
import { alertToast } from '@/utilities/notification'
import { formatNumber } from '@/utilities/number'
import useApiRequest from '@/composables/useApiRequest'
import { getDictionaryItem, getDictionaryOptions } from '@/dictionary'
import type { AgreementOpportunityItem } from '@/dictionary'
import { useRoute, useRouter } from 'vue-router'
import dayjs from 'dayjs'
import { capitalize } from '@/utilities/string'
import Swal from 'sweetalert2'
import Metric from '@/components/metrics/Metric.vue'
import Skeleton from '@/components/skeleton/Skeleton.vue'

const route = useRoute()
const router = useRouter()
const campaignID = ref(route.params.campid)
const campaignColumnsStorageKey = 'campaign-prospect-columns'

type CampaignOpportunitySnapshot = {
  equity: number | null
  label: string | null
  offerId?: number
}

type CampaignOpportunity = {
  default: CampaignOpportunitySnapshot | null
  withBestOffer: CampaignOpportunitySnapshot | null
}

type CampaignColumn = {
  field: string
  header: string
  hideByDefault: boolean
  campaignTypes?: Array<'standard' | 'marketing'>
}

const allAgreementTags = useApiRequest()
const showArchivedCustomers = ref(false)
const includeOffers = ref(true)
const dt = ref({ processedData: [] })

const { isLoading: campaignIsLoading, data: campaignData, send: campaignFetchData } = useApiRequest()

onMounted(() => {
  allAgreementTags.send({ endpoint: '/v1/tags' })
  campaignFetchData({ endpoint: '/v1/campaigns/' + campaignID.value })

  FilterService.register(tagsFilter.value, (value, filter) => {
    if (!filter.length) {
      return true
    }
    return value.some(tag => filter.includes(tag.id))
  })
})

const tagsFilter = ref('Tags Filter')
const filters = ref([])
const columnsSelected = ref<CampaignColumn[]>([])

const columns = computed<CampaignColumn[]>(() => {
  const availableColumns: CampaignColumn[] = [
    { field: 'name', header: 'Customer', hideByDefault: false },
    { field: 'monthsRemaining', header: 'Months Remaining', hideByDefault: false },
    { field: 'contractType', header: 'Product', hideByDefault: true },
    { field: 'equity', header: 'Equity', hideByDefault: true },
    { field: 'opportunity', header: 'Opportunity', hideByDefault: false },
    { field: 'status', header: 'Status', hideByDefault: false },
    { field: 'tags', header: 'Tags', hideByDefault: true },
    {
      field: 'lastCalled',
      header: 'Last Called',
      hideByDefault: false,
      campaignTypes: ['standard']
    },
    {
      field: 'latestAppointment',
      header: 'Appointment',
      hideByDefault: false,
      campaignTypes: ['standard']
    },
    {
      field: 'inviteStatus',
      header: 'Invite Status',
      hideByDefault: false,
      campaignTypes: ['marketing']
    }
  ]

  return availableColumns.filter(column => {
    if (!column.campaignTypes?.length) {
      return true
    }

    return campaignData.value?.type ? column.campaignTypes.includes(campaignData.value.type) : false
  })
})

function getStoredColumnFields(): string[] | null {
  const storedColumns = localStorage.getItem(campaignColumnsStorageKey)

  if (!storedColumns) {
    return null
  }

  try {
    const parsedColumns = JSON.parse(storedColumns)
    return Array.isArray(parsedColumns) ? parsedColumns : null
  } catch {
    return null
  }
}

function updateSelectedColumns(selectedFields: string[]) {
  columnsSelected.value = columns.value.filter(column => selectedFields.includes(column.field))
}

function isColumnVisible(field: string): boolean {
  return columnsSelected.value.some(column => column.field === field)
}

function onToggle(selectedColumns: CampaignColumn[]) {
  columnsSelected.value = columns.value.filter(column =>
    selectedColumns.some(selectedColumn => selectedColumn.field === column.field)
  )

  localStorage.setItem(campaignColumnsStorageKey, JSON.stringify(columnsSelected.value.map(column => column.field)))
}

watch(
  columns,
  availableColumns => {
    const storedColumnFields = getStoredColumnFields()

    if (storedColumnFields !== null) {
      updateSelectedColumns(storedColumnFields)
      return
    }

    columnsSelected.value = availableColumns.filter(column => !column.hideByDefault)
  },
  { immediate: true }
)

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    contractType: { value: null, matchMode: FilterMatchMode.IN },
    displayOpportunityLabel: { value: null, matchMode: FilterMatchMode.IN },
    status: {
      value: ['Idle', 'Unresponsive', 'Uncontactable', 'Contacted', 'Appointed'],
      matchMode: FilterMatchMode.IN
    },
    tags: { value: null, matchMode: tagsFilter.value },
    inviteStatus: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

function clearFilters() {
  initFilters()
  // set archived customers to false
  showArchivedCustomers.value = false
  includeOffers.value = false
}

/**
 * Returns the currently selected opportunity snapshot for a prospect.
 */
function getSelectedOpportunitySnapshot(
  opportunity: CampaignOpportunity | null | undefined
): CampaignOpportunitySnapshot | null {
  if (!opportunity) {
    return null
  }

  if (includeOffers.value) {
    return opportunity.withBestOffer ?? opportunity.default ?? null
  }

  return opportunity.default ?? opportunity.withBestOffer ?? null
}

const prospectsComputed = computed(() => {
  const prospects = campaignData.value?.prospects ?? []

  const mappedProspects = prospects.map(prospect => {
    const tags: Array<{ id: number; title: string; displayStyle: string }> = []
    prospect.tagIDs.forEach(tagID => {
      if (allAgreementTags.data.value) {
        const tagData = allAgreementTags.data.value.find(tag => tag.id === tagID)
        if (tagData) {
          tags.push(tagData)
        }
      }
    })
    const monthsRemaining = dayjs(prospect.endDate).diff(dayjs(), 'month')
    const termCompletion = 100 - (monthsRemaining / prospect.term) * 100
    const selectedOpportunity = getSelectedOpportunitySnapshot(prospect.opportunity as CampaignOpportunity | null)
    const displayOpportunityLabel = selectedOpportunity?.label ?? null
    const opportunityWeight = displayOpportunityLabel
      ? (getDictionaryItem<AgreementOpportunityItem>('agreementOpportunity', displayOpportunityLabel)?.weight ?? 0)
      : 0
    const startDate = dayjs(prospect.endDate).subtract(prospect.term, 'month').format('YYYY-MM-DD')
    let lastCallData = {
      text: '',
      tippy: 'Not called yet',
      color: 'secondary'
    }
    if (prospect.lastCalled) {
      const diff = dayjs(dayjs()).diff(dayjs(prospect.lastCalled), 'day')
      let color = ''
      if (diff >= 30 && diff < 60) {
        color = 'warning-primary'
      }
      if (diff > 60) {
        color = 'error-primary'
      }
      lastCallData = {
        text: dayjs(prospect.lastCalled).fromNow(),
        tippy: dayjs(prospect.lastCalled).format('DD-MM-YYYY HH:MM'),
        color: color
      }
    }
    let inviteStatusColor: string | undefined
    let inviteStatusIcon: string | undefined
    if (prospect.inviteStatus == 'sent') {
      inviteStatusColor = 'secondary'
      inviteStatusIcon = 'paper-plane'
    } else if (prospect.inviteStatus == 'opened') {
      inviteStatusColor = 'info'
      inviteStatusIcon = 'envelope-open'
    } else if (prospect.inviteStatus == 'clicked') {
      inviteStatusColor = 'warning'
      inviteStatusIcon = 'bullseye-pointer'
    } else if (prospect.inviteStatus == 'booked') {
      inviteStatusColor = 'primary'
      inviteStatusIcon = 'calendar-check'
    }

    return {
      ...prospect,
      tags,
      monthsRemaining,
      termCompletion,
      opportunityWeight,
      startDate,
      lastCallData,
      inviteStatusColor,
      inviteStatusIcon,
      hasVehicleOffer: Boolean((prospect.opportunity as CampaignOpportunity | null)?.withBestOffer?.offerId),
      vehicleOfferEquity: (prospect.opportunity as CampaignOpportunity | null)?.withBestOffer?.equity ?? null,
      displayEquity: selectedOpportunity?.equity ?? null,
      displayOpportunityLabel
    }
  })

  if (showArchivedCustomers.value) {
    return mappedProspects
  } else {
    return mappedProspects.filter(prospect => prospect.isArchived == false)
  }
})

// Computed metrics based on the (filtered) prospects list
const metrics = computed(() => {
  const prospects = prospectsComputed.value || []
  return {
    totalCustomers: prospects.length,
    contacted: prospects.filter(p => p.status === 'Contacted' || p.status === 'Appointed' || p.status === 'Dealt')
      .length,
    appointed: prospects.filter(p => p.status === 'Appointed' || p.status === 'Dealt').length,
    dealt: prospects.filter(p => p.status === 'Dealt').length,
    archived: prospects.filter(p => p.isArchived).length
  }
})

const contractTypesList = ref(['PCP', 'HP', 'CH'])
const statusesList = ref([
  'Idle',
  'Unresponsive',
  'Uncontactable',
  'Contacted',
  'Appointed',
  'Dealt',
  'Refinanced',
  'Settled',
  'Handed Back'
])
const tagsList = ref(allAgreementTags.data)
const opportunityOptions = getDictionaryOptions<AgreementOpportunityItem>('agreementOpportunity').filter(
  option => option.key !== 'ERROR'
)

const inviteStatusOptions = computed(() => {
  return ['sent', 'opened', 'clicked', 'booked'].map(status => ({
    value: status,
    label: capitalize(status)
  }))
})

function deleteCampaign() {
  Swal.fire({
    title: 'Are you sure?',
    icon: 'warning',
    reverseButtons: true,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes, delete!',
    confirmButtonColor: 'var(--bg-error-solid)',
    cancelButtonText: 'Cancel'
  }).then(function (response) {
    if (response.isConfirmed) {
      useApiRequest()
        .send({ endpoint: '/v1/campaigns/' + campaignID.value, method: 'DELETE' })
        .then(() => {
          router.push('/customers/campaigns')
        })
    }
  })
}

function removeProspect(id: number) {
  Swal.fire({
    title: 'Are you sure?',
    icon: 'warning',
    reverseButtons: true,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes, remove!',
    confirmButtonColor: 'var(--bg-error-solid)',
    cancelButtonText: 'Cancel'
  }).then(function (response) {
    if (response.isConfirmed) {
      useApiRequest()
        .send({
          endpoint: '/v1/campaigns/' + campaignID.value + '/prospects/' + id,
          method: 'DELETE'
        })
        .then(response => {
          if (!response.success) {
            alertToast('Failed to remove prospect', response.data?.message, 'error')
          }
          alertToast('Prospect removed', null, 'success')
          const prospect = campaignData.value.prospects.find(prospect => prospect.id == id)
          const index = campaignData.value.prospects.indexOf(prospect)
          if (index > -1) {
            campaignData.value.prospects.splice(index, 1)
          }
        })
    }
  })
}
</script>

<style>
.toggle-container {
  border: 1px solid var(--border-secondary);
  border-radius: var(--rounded-md);
  padding: var(--s-2) var(--s-3) var(--s-2) var(--s-2);
  text-align: center;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-secondary);
}
</style>
